import { logger } from "./loggingUtils";

export function getCurrentSectionFromPath(pathname: string): string {
  const pathParts = pathname.split("/");
  const lastPart = pathParts[pathParts.length - 1];

  // Handle nested routes like "apply/results" - should return "apply"
  if (lastPart === "results" && pathParts[pathParts.length - 2] === "apply") {
    return "apply";
  }

  return lastPart;
}

/**
 * Confirm DOM has Learnosity elements.
 * @param items Array of Learnosity Item IDs as strings.
 * @param retryCount Current attempt iteration.
 * @param maxRetries How many times to re-attempt before bailing.
 * @returns True if all elements are in the DOM, null otherwise.
 */
export const checkDOMItemsReady = (
  items: string[],
  retryCount: number = 0,
  maxRetries: number = 50
) => {
  if (items && items.length >= 1) {
    const results = items.map(itemId => {
      const element = document.querySelector(`[data-reference="${itemId}"]`);
      return element !== null;
    });

    const allElementsExist = results.every(Boolean);
    if (allElementsExist) {
      logger.log("%cAll Learnosity IDs exist in DOM", "color: #B6DE83");
      return true;
    }
  } else {
    logger.warn("Learnosity item array invalid or empty");
  }

  if (retryCount + 1 < maxRetries) {
    logger.log(
      `%cSome Learnosity IDs cannot be found in DOM, checking ${maxRetries - retryCount} more times`,
      "color: #F7D44F"
    );
    setTimeout(
      () => checkDOMItemsReady(items, retryCount + 1, maxRetries),
      100
    );
  } else {
    logger.warn("Could not parse Learnosity elements.");
  }
};

export interface WaitForDomItemsReadyOptions {
  timeout?: number;
  pollInterval?: number;
  attemptId?: number;
}

export function waitForDomItemsReady(
  itemReferences: string[],
  {
    timeout = 10_000,
    pollInterval = 100,
    attemptId
  }: WaitForDomItemsReadyOptions = {}
): Promise<boolean> {
  return new Promise(resolve => {
    if (!Array.isArray(itemReferences) || itemReferences.length === 0) {
      logger.warn(
        `[DOM Check] (attempt: ${attemptId}) invalid or empty item references`
      );
      resolve(false);
      return;
    }

    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;

      const allReady = itemReferences.every(ref =>
        document.querySelector(`[data-reference="${ref}"]`)
      );

      if (allReady) {
        logger.log(
          `%c[DOM Check] (attempt: ${attemptId}) DOM ready`,
          "color: #B6DE83"
        );
        resolve(true);
        return;
      }

      if (elapsed >= timeout) {
        logger.error(
          `[DOM Check] (attempt: ${attemptId}) DOM readiness timed out`,
          { elapsed, itemReferences }
        );
        resolve(false);
        return;
      }

      setTimeout(tick, pollInterval);
    };

    logger.log(`[DOM Check] (attempt: ${attemptId}) waiting for DOM`, {
      itemReferences
    });

    tick();
  });
}

export type AccordionSection = { title: string; content: string };

/**
 * Takes an array and breaks it into sections based on an HTML tag.
 * @param elements Array of Element to check through.
 * @param tag HTML tag to use as the separator for sections.
 * @returns Array of `AccordionSection` objects, title is the separator content, content is the remaining HTML until the next separator.
 */
export const parseSections = (
  elements: Element[],
  tag: string
): AccordionSection[] => {
  const sections: AccordionSection[] = [];
  let currentSection: AccordionSection | null = null;

  for (const elem of elements) {
    if (elem.tagName.toLowerCase() === tag.toLowerCase()) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: elem.textContent?.trim() ?? "",
        content: ""
      };
    } else if (currentSection) {
      currentSection.content += elem.outerHTML;
    }
  }

  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
};

/**
 * Watches a container for the children to appear, creates an array splitting content by specified HTML tag (H3 by default).
 * @param container HTML element to observe.
 * @param tag Name of HTML tag to separate content into sections. Defaults to `"h3"`.
 * @param timeout Time in milliseconds to wait before bailing. Defaults to `5000`.
 * @returns An array of `AccordionSection` objects containing the tag content as the title, and all other HTML until the next section as the content.
 */
export const getLearnositySectionsByTag = (
  container: Element,
  tag: string = "h3",
  timeout: number = 5000
): Promise<AccordionSection[]> => {
  return new Promise(resolve => {
    let resolved = false;

    const finish = (sections: Element[]) => {
      if (!resolved) {
        try {
          resolved = true;
          observer.disconnect();
          clearTimeout(fallbackTimeout);
          resolve(parseSections(sections, tag));
        } catch {
          logger.warn("Could not parse Learnosity elements.");
          resolve([]);
        }
      }
    };

    const fallbackTimeout = setTimeout(() => {
      finish([]);
    }, timeout);

    const observer = new MutationObserver(() => {
      if (container && container.children.length > 0) {
        finish(Array.from(container.children));
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: false
    });

    if (container.children.length > 0) {
      finish(Array.from(container.children));
    }
  });
};

/**
 * Parses an HTML string and splits it into sections based on H3 tags.
 * @param htmlString The HTML string to parse.
 * @param tag HTML tag to use as the separator for sections. Defaults to "h3".
 * @returns Array of `AccordionSection` objects with title and content.
 */
export const parseHtmlStringSections = (
  htmlString: string,
  tag: string = "h3"
): AccordionSection[] => {
  if (!htmlString) return [];

  // Create a temporary container to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Get all child elements
  const elements = Array.from(tempDiv.children);

  return parseSections(elements, tag);
};

/**
 * Resolve textbook feature HTML into accordion sections for rendering.
 *
 * Read Aloud render-gate fix (BLD2-458): when the textbook content has no
 * `<h3>` headings, `parseHtmlStringSections` returns `[]` — which previously
 * collapsed `hasTextbook` to false and hid the entire textbook tab + Read Aloud
 * player, and dropped any content before the first heading. Fall back to a
 * single section so the content (and the player) still render.
 *
 * @param textbookContent The textbook feature HTML (or null when absent).
 * @returns Parsed sections, or a single fallback section, or `[]` when empty.
 */
export const getTextbookSections = (
  textbookContent: string | null
): AccordionSection[] => {
  if (!textbookContent) return [];

  const parsed = parseHtmlStringSections(textbookContent, "h3");

  return parsed.length > 0
    ? parsed
    : [{ title: "Textbook", content: textbookContent }];
};

/**
 * Derive whether a lesson exposes a textbook section for Read Aloud (BLD2-458).
 *
 * Uses the curriculum-level textbook activity item as the primary signal (the
 * reliable indicator that a textbook exists) rather than `<h3>` content
 * parsing, so a textbook whose content lacks `<h3>` headings no longer
 * disappears. Content presence + parsed sections remain a secondary signal.
 *
 * @param textbookActivityItem The textbook activity item id (curriculum signal).
 * @param textbookContent The textbook feature HTML (or null when absent).
 * @param textbookSections The resolved sections from `getTextbookSections`.
 */
export const deriveHasTextbook = (
  textbookActivityItem: string | null | undefined,
  textbookContent: string | null,
  textbookSections: AccordionSection[]
): boolean =>
  Boolean(textbookActivityItem) ||
  (textbookContent !== null && textbookSections.length > 0);

export { calcLessonScore, type LessonScoreSummary } from "@shared/learnosity/utils/calcLessonScore";
